import { useEffect } from 'react';
import { ensureMe } from './me';
import { referralToStore, saveReferral } from './referral-capture';

/**
 * Catches an invite's `?ref=<sharer metaSiteId>` off the URL and puts it in local storage, to be spent
 * when this person later connects a phone (see `use-referral-claim.ts`).
 *
 * Read from `window.location` rather than the router, and in an effect that runs before anything else on
 * the page navigates: `/welcome` replaces its own URL with the bare mount as soon as the recipient is
 * created, so the query is gone within a second of arriving. Reading it here is the only chance.
 *
 * It is bound to the signed-in user when that can be resolved, so a stranger who uses this browser next
 * cannot collect the invite. Resolving that is a network call, so the id is stored FIRST, unbound, and
 * upgraded when the identity lands — losing the credit because a `/api/me` call was slow would be the
 * worse of the two failures, and the payout is capped at one per phone regardless.
 */
export function useCaptureReferrer(): void {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const raw = new URLSearchParams(window.location.search).get('ref');
    if (!raw) return;

    const unbound = referralToStore({ raw, userId: '', now: Date.now() });
    if (!unbound) return;
    saveReferral(unbound);

    let cancelled = false;
    void ensureMe().then((me) => {
      if (cancelled || !me?.userId) return;
      saveReferral({ ...unbound, userId: me.userId });
    });
    return () => {
      cancelled = true;
    };
  }, []);
}
